// Releases the server-side lock behind a stuck match-loading screen.
//
// After a client crash during match creation, the server still holds a
// part-created match: the in-memory room binding re-triggers
// session:active on every reconnect (re-mounting the board), and the
// instance row stuck phase='active' makes the reconcile heartbeat
// auto-launch it on every boot. The re-mounted board can never finish
// loading (no roomInfo decks, no snapshot), so without an explicit
// release the account is pinned until a server-side sweep clears it.
//
// Escaping must release BOTH halves:
//   1. Pause the instance row (pause, NOT forfeit: it lands in the
//      saved-games list, and the reconcile heartbeat never auto-launches
//      a paused instance).
//   2. Send room:leave. The server answers by destroying the room and
//      dropping the playerRooms binding, which stops session:active.
//
// The session:active rejoin only carries an instanceId for solo rooms,
// so when the caller doesn't know the instance, resolve it from the
// player's instance list by room code.
//
// The `deps` parameter exists for tests only.

import { listInstances, pauseInstance } from './instancesApi';
import { send } from '../serverClient';

export async function releaseStuckMatch({ roomCode = null, instanceId = null, deps = {} } = {}) {
  const {
    listInstancesFn = listInstances,
    pauseInstanceFn = pauseInstance,
    sendFn = send,
  } = deps;

  let resolvedId = instanceId;
  if (!resolvedId && roomCode) {
    try {
      const instances = await listInstancesFn();
      const match = (instances || []).find(
        (i) => i.room_code === roomCode && (i.phase === 'active' || i.phase === 'paused'),
      );
      resolvedId = match?.id || null;
    } catch (err) {
      console.warn('[stuck-match] instance lookup failed:', err?.message || err);
    }
  }

  if (resolvedId) {
    try {
      await pauseInstanceFn(resolvedId);
    } catch (err) {
      console.warn('[stuck-match] pauseInstance failed:', err?.message || err);
    }
  }

  // Always leave the room, even when no instance resolved. The server's
  // leaveRoom keys off the caller's own binding, and destroying the room
  // pauses an active instance as a fallback anyway.
  try {
    sendFn('room:leave', { roomCode });
  } catch (err) {
    console.warn('[stuck-match] room:leave failed:', err?.message || err);
  }

  return resolvedId;
}
